import { colorPalette } from "@wish-ui";
import { PAGE_URLS } from "utils/constants";
import { CAMPAIGN_THEMES } from "./campaigns";
import { CampaignTheme, HomeHeaderStyle, HomeHeroSlot } from "./types";

export * from "./types";

export const DEFAULT_HOME_HEADER_STYLE: HomeHeaderStyle = {
  background: colorPalette.popWhite[500],
  appearance: "light",
};

export const SPLASH_INTRO_PLAYED_SESSION_KEY = "campaignSplashIntroPlayed";

/**
 * Universal feature in the in-house feature flag service that picks the live
 * campaign: its highest-weight enabled variant names a theme by `id`. Any
 * other variant (e.g. DISABLED), no variant, or a missing or disabled feature
 * means no campaign.
 */
export const CAMPAIGN_THEME_FEATURE = "CAMPAIGN_THEME";

/** localStorage key for the last variant the flag server answered with. */
export const CAMPAIGN_THEME_VARIANT_STORAGE_KEY = "campaignThemeVariant";

// Cold starts land on one of these; deep links elsewhere skip the intro.
const SPLASH_INTRO_ENTRY_PATHS = ["/", "/index.html", PAGE_URLS.HOME];

export const getCampaignThemeById = (
  id: string | null | undefined,
  campaigns: CampaignTheme[] = CAMPAIGN_THEMES
): CampaignTheme | null =>
  id ? campaigns.find((campaign) => campaign.id === id) ?? null : null;

/**
 * The variant the flag server answered with: its name, or null when the
 * server says there is none (no enabled variant, or a missing or disabled
 * feature, which it answers with a 404). Undefined while loading or after a
 * network or server error, when the answer is not known.
 */
export const getServerThemeVariant = ({
  data,
  error,
  isSuccess,
}: {
  data?: { variant_name?: string | null } | null;
  error?: unknown;
  isSuccess: boolean;
}): string | null | undefined => {
  if (isSuccess) return data?.variant_name ?? null;
  if ((error as { status?: unknown } | undefined)?.status === 404) return null;
  return undefined;
};

export const getHomeHeroSlot = (
  campaign: CampaignTheme | null,
  isOnboardingHome: boolean
): HomeHeroSlot | undefined =>
  (isOnboardingHome && campaign?.slots.onboardingHomeHero) ||
  campaign?.slots.homeHero;

/**
 * The themed header is tied to the campaign hero banner: it shows while the
 * banner is in view. Without a banner the hero can collapse to zero height
 * (e.g. no commissions card), so the header stays default.
 */
export const hasThemedHomeHeader = (campaign: CampaignTheme | null) =>
  !!campaign?.slots.homeHeader && !!campaign.slots.homeHero;

export const getHomeHeaderStyle = (
  campaign: CampaignTheme | null,
  isHeroInView: boolean
): HomeHeaderStyle =>
  campaign?.slots.homeHeader && hasThemedHomeHeader(campaign) && isHeroInView
    ? campaign.slots.homeHeader
    : DEFAULT_HOME_HEADER_STYLE;

export const shouldShowSplashIntro = ({
  campaign,
  isNativeApp,
  pathname,
  hasPlayedThisSession,
}: {
  campaign: CampaignTheme | null;
  isNativeApp: boolean;
  pathname: string;
  hasPlayedThisSession: boolean;
}) =>
  !!campaign?.slots.splashIntro &&
  isNativeApp &&
  !hasPlayedThisSession &&
  SPLASH_INTRO_ENTRY_PATHS.includes(pathname);
